import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, FileText, CheckCircle, AlertCircle, Trash2 } from "lucide-react";

interface NewaygoCountyTextParserProps {
  onImportComplete?: () => void;
}

interface ParsedRecord {
  filingDate: string;
  documentNumber: string;
  documentType: string;
  sellerName: string;
  buyerName: string;
  legalDescription: string;
  township: string;
  parcelNumber: string;
  hasAdditional: boolean;
}

interface ImportResult {
  totalParsed: number;
  duplicatesSkipped: number;
  additionalFiltered: number;
  familyFiltered: number;
  existingSkipped: number;
  imported: number;
}

// Bank and institutional keywords
const BANK_KEYWORDS = [
  'BANK', 'MORTGAGE', 'CREDIT UNION', 'FEDERAL', 'SAVINGS', 'LOAN',
  'FINANCIAL', 'LENDING', 'CAPITAL', 'FUNDING', 'FANNIE', 'FREDDIE',
  'HUD', 'VETERANS', 'FHA', 'RURAL DEVELOPMENT'
];

// Business entity keywords
const BUSINESS_KEYWORDS = ['LLC', 'INC', 'CORP', 'COMPANY', 'LTD', 'HOLDINGS', 'PROPERTIES', 'ENTERPRISES'];

export function NewaygoCountyTextParser({ onImportComplete }: NewaygoCountyTextParserProps) {
  const [rawText, setRawText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const { toast } = useToast();

  const parseDate = (dateStr: string): string => {
    // Format: MM/DD/YYYY -> YYYY-MM-DD
    const parts = dateStr.trim().split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  };

  const reverseName = (name: string): string => {
    // Convert "LAST, FIRST MIDDLE" to "FIRST MIDDLE LAST"
    // Handle cases like "BROOKS, MARLYS" -> "MARLYS BROOKS"
    // Also handle "DUNN TRUST, SUSAN M, TRUSTEE" -> keep as is (multiple commas = complex)
    const commaCount = (name.match(/,/g) || []).length;
    
    if (commaCount === 1) {
      const [lastName, firstName] = name.split(',').map(s => s.trim());
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      }
    }
    
    // For complex names with multiple commas or no commas, return as-is
    return name;
  };

  const extractLastName = (name: string): string => {
    // Extract last name from format "LASTNAME, FIRSTNAME" or "LASTNAME TRUST"
    const cleaned = name.replace(/,.*$/, '').trim();
    return cleaned.split(/\s+/)[0].toUpperCase();
  };

  const isFamilyTransfer = (seller: string, buyer: string): boolean => {
    const sellerLastName = extractLastName(seller);
    const buyerLastName = extractLastName(buyer);
    
    if (sellerLastName === buyerLastName && sellerLastName.length > 1) {
      return true;
    }
    
    // Check for trust/estate patterns that indicate family
    const familyPatterns = ['ESTATE', 'HEIR', 'SUCCESSOR'];
    const sellerUpper = seller.toUpperCase();
    const buyerUpper = buyer.toUpperCase();
    
    for (const pattern of familyPatterns) {
      if (sellerUpper.includes(pattern) || buyerUpper.includes(pattern)) {
        if (sellerLastName === buyerLastName) {
          return true;
        }
      }
    }
    
    return false;
  };

  const isBankTransaction = (seller: string, buyer: string): boolean => {
    const combined = (seller + ' ' + buyer).toUpperCase();
    return BANK_KEYWORDS.some(keyword => combined.includes(keyword));
  };

  const isBusinessBuyer = (buyer: string): boolean => {
    const buyerUpper = buyer.toUpperCase();
    return BUSINESS_KEYWORDS.some(keyword => buyerUpper.includes(keyword));
  };

  const parseNewaygoText = (text: string): ParsedRecord[] => {
    const records: ParsedRecord[] = [];
    const lines = text.split('\n');
    
    let currentRecord: Partial<ParsedRecord> = {};
    let i = 0;
    
    console.log('Parsing', lines.length, 'lines');
    
    while (i < lines.length) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Look for date and document number pattern - flexible with whitespace and doc type
      // Examples:
      // "12/30/2021    00487-00013                     LAND CONTRACT"
      // "05/07/2020    00478-00741                     MEMORANDUM OF LAND CONTRACT"
      const headerMatch = trimmedLine.match(
        /^(\d{1,2}\/\d{1,2}\/\d{4})\s+(\d{5}-\d{5})\s+(.+?)\s*$/i
      );

      if (headerMatch && headerMatch[3].toUpperCase().includes('LAND CONTRACT')) {
        // Save previous record if exists
        if (currentRecord.documentNumber && currentRecord.sellerName && currentRecord.buyerName) {
          records.push(currentRecord as ParsedRecord);
        }

        currentRecord = {
          filingDate: parseDate(headerMatch[1]),
          documentNumber: headerMatch[2],
          documentType: headerMatch[3].trim().toUpperCase(),
          sellerName: '',
          buyerName: '',
          legalDescription: '',
          township: '',
          parcelNumber: '',
          hasAdditional: false
        };

        console.log('Found record:', headerMatch[2], 'type:', currentRecord.documentType);
        i++;
        continue;
      }
      
      // Parse SELLER line - look for "SELLER" followed by name
      // Format: "   SELLER           DUNN TRUST, SUSAN M" or " X SELLER           DUNN TRUST"
      const sellerMatch = trimmedLine.match(/^[X\s]*SELLER\s+(.+)$/i);
      if (sellerMatch && currentRecord.documentNumber && !currentRecord.sellerName) {
        currentRecord.sellerName = reverseName(sellerMatch[1].trim());
        i++;
        continue;
      }
      
      // Parse BUYER line
      const buyerMatch = trimmedLine.match(/^[X\s]*BUYER\s+(.+)$/i);
      if (buyerMatch && currentRecord.documentNumber && !currentRecord.buyerName) {
        currentRecord.buyerName = reverseName(buyerMatch[1].trim());
        i++;
        continue;
      }
      
      // Parse SEC line (legal description with township)
      const secMatch = trimmedLine.match(/^(SEC\s+\d+\s+T\d+N\s+R\d+W\s+.+?)\s{2,}(.+TOWNSHIP|CITY OF .+)/i);
      if (secMatch && currentRecord.documentNumber && !currentRecord.legalDescription) {
        currentRecord.legalDescription = secMatch[1].trim();
        currentRecord.township = secMatch[2].trim();
        i++;
        continue;
      }
      
      // Parse LOT line (alternative legal description)
      const lotMatch = trimmedLine.match(/^(LOTS?\s+[\d,-]+(?:,?\s*BLOCK\s*\d+)?)\s{2,}(.+TOWNSHIP|CITY OF .+)/i);
      if (lotMatch && currentRecord.documentNumber && !currentRecord.legalDescription) {
        currentRecord.legalDescription = lotMatch[1].trim();
        currentRecord.township = lotMatch[2].trim();
        i++;
        continue;
      }
      
      // Check for ADDITIONAL marker - only filter if it contains "REFERRAL"
      // Filter: "ADDITIONAL PARTIES AND REFERRALS" or "ADDITIONAL REFERRAL"
      // Keep: "ADDITIONAL PARTIES" only (no referral)
      if (trimmedLine.includes('>>>>>> ADDITIONAL')) {
        // Only mark for filtering if line contains REFERRAL
        if (currentRecord.documentNumber && trimmedLine.toUpperCase().includes('REFERRAL')) {
          currentRecord.hasAdditional = true;
        }
        
        // Extract parcel number from this line
        const parcelMatch = trimmedLine.match(/(\d{2}-\d{2}-\d{2}-\d{3}-\d{3})$/);
        if (parcelMatch && currentRecord.documentNumber) {
          currentRecord.parcelNumber = parcelMatch[1];
        }
        i++;
        continue;
      }
      
      // Check for parcel number at end of line (standalone)
      const parcelOnlyMatch = trimmedLine.match(/^(\d{2}-\d{2}-\d{2}-\d{3}-\d{3})$/);
      if (parcelOnlyMatch && currentRecord.documentNumber && !currentRecord.parcelNumber) {
        currentRecord.parcelNumber = parcelOnlyMatch[1];
        i++;
        continue;
      }
      
      i++;
    }
    
    // Don't forget the last record
    if (currentRecord.documentNumber && currentRecord.sellerName && currentRecord.buyerName) {
      records.push(currentRecord as ParsedRecord);
    }
    
    console.log('Total records parsed:', records.length);
    return records;
  };

  const processRecords = async () => {
    if (!rawText.trim()) {
      toast({
        title: "No data",
        description: "Please paste some data to import",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      // Parse all records
      const allRecords = parseNewaygoText(rawText);
      setProgress(20);
      
      const result: ImportResult = {
        totalParsed: allRecords.length,
        duplicatesSkipped: 0,
        additionalFiltered: 0,
        familyFiltered: 0,
        existingSkipped: 0,
        imported: 0
      };

      // Deduplicate by document number - keep only first occurrence
      const uniqueRecords = new Map<string, ParsedRecord>();
      for (const record of allRecords) {
        if (!uniqueRecords.has(record.documentNumber)) {
          uniqueRecords.set(record.documentNumber, record);
        } else {
          result.duplicatesSkipped++;
        }
      }
      setProgress(30);

      // Filter out records with ADDITIONAL markers
      const filteredRecords: ParsedRecord[] = [];
      for (const record of uniqueRecords.values()) {
        if (record.hasAdditional) {
          result.additionalFiltered++;
        } else {
          filteredRecords.push(record);
        }
      }
      setProgress(40);

      // Apply owner-finance filters
      const validRecords: ParsedRecord[] = [];
      for (const record of filteredRecords) {
        if (isBankTransaction(record.sellerName, record.buyerName)) {
          result.familyFiltered++;
          continue;
        }
        if (isBusinessBuyer(record.buyerName)) {
          result.familyFiltered++;
          continue;
        }
        if (isFamilyTransfer(record.sellerName, record.buyerName)) {
          result.familyFiltered++;
          continue;
        }
        validRecords.push(record);
      }
      setProgress(50);

      // Check for existing records in database
      const docNumbers = validRecords.map(r => r.documentNumber);
      const { data: existing } = await supabase
        .from('county_research')
        .select('document_number')
        .eq('county', 'newaygo')
        .in('document_number', docNumbers);

      const existingDocNumbers = new Set(existing?.map(e => e.document_number) || []);
      setProgress(60);

      // Insert new records
      const recordsToInsert = validRecords.filter(r => {
        if (existingDocNumbers.has(r.documentNumber)) {
          result.existingSkipped++;
          return false;
        }
        return true;
      });

      if (recordsToInsert.length > 0) {
        const insertData = recordsToInsert.map(record => ({
          county: 'newaygo',
          document_number: record.documentNumber,
          document_type: record.documentType,
          filing_date: record.filingDate,
          grantor_name: record.sellerName,
          grantee_name: record.buyerName,
          legal_description: record.legalDescription || null,
          property_address: record.township || null,
          notes: record.parcelNumber ? `Parcel: ${record.parcelNumber}` : null,
          is_potential_owner_finance: true,
          confidence_score: 80,
          detection_reasons: JSON.stringify(['Land Contract document type', 'Individual seller to individual buyer']),
          search_date: new Date().toISOString()
        }));

        const batchSize = 50;
        for (let i = 0; i < insertData.length; i += batchSize) {
          const batch = insertData.slice(i, i + batchSize);
          const { error } = await supabase.from('county_research').insert(batch);
          
          if (error) {
            console.error('Insert error:', error);
            throw error;
          }
          
          result.imported += batch.length;
          setProgress(60 + ((i + batch.length) / insertData.length) * 35);
        }
      }

      setProgress(100);
      setImportResult(result);
      
      toast({
        title: "Import complete",
        description: `Imported ${result.imported} records from Newaygo County`
      });

      if (onImportComplete) {
        onImportComplete();
      }

    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import failed",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    setRawText(text);
    toast({
      title: "File loaded",
      description: `Loaded ${file.name} - ${text.split('\n').length} lines`
    });
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Alert className={importResult.imported > 0 ? "border-green-500" : "border-yellow-500"}>
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Import Results</AlertTitle>
          <AlertDescription>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div>Total Records Parsed: <strong>{importResult.totalParsed}</strong></div>
              <div>Duplicates Skipped: <strong>{importResult.duplicatesSkipped}</strong></div>
              <div>Additional Party Records Filtered: <strong>{importResult.additionalFiltered}</strong></div>
              <div>Family/Bank/Business Filtered: <strong>{importResult.familyFiltered}</strong></div>
              <div>Already in Database: <strong>{importResult.existingSkipped}</strong></div>
              <div className="text-green-600">Successfully Imported: <strong>{importResult.imported}</strong></div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Newaygo County, MI - Land Contract Parser
          </CardTitle>
          <CardDescription>
            Paste land contract search results from Newaygo County. Automatically removes duplicates, 
            filters out "ADDITIONAL PARTIES" records, and excludes family transfers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <label className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Upload Text File
                <input
                  type="file"
                  accept=".txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setRawText("")}
              disabled={!rawText}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>

          <Textarea
            placeholder={`Paste Newaygo County land contract search results here...

Example format:
   12/30/2021    00487-00013                     LAND CONTRACT
   SELLER           DUNN TRUST, SUSAN M, TRUSTEE
 X BUYER            DUNN, JOSHUA
   
   SEC 04  T11N  R11W  THE SW OF THE SW QTR           ENSLEY TOWNSHIP
>>>>>> ADDITIONAL PARTIES AND REFERRALS               62-24-04-300-003`}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            className="min-h-[300px] font-mono text-xs"
          />

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">
                Processing... {Math.round(progress)}%
              </p>
            </div>
          )}

          <Button 
            onClick={processRecords} 
            disabled={isProcessing || !rawText.trim()}
            className="w-full"
          >
            {isProcessing ? "Processing..." : "Parse & Import Records"}
          </Button>
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Expected Format</AlertTitle>
        <AlertDescription className="text-xs space-y-1">
          <p>• Each record starts with a date, document number, and "LAND CONTRACT"</p>
          <p>• SELLER and BUYER lines follow (X marks the primary party)</p>
          <p>• Legal description includes SEC/LOT info and township name</p>
          <p>• Parcel number appears at the end (XX-XX-XX-XXX-XXX format)</p>
          <p className="mt-2 font-medium">Filtering:</p>
          <p>• Records with "ADDITIONAL PARTIES/REFERRALS" are filtered out</p>
          <p>• Only first occurrence of each document number is kept</p>
          <p>• Family transfers (same last name) are excluded</p>
          <p>• Bank transactions and business buyers are excluded</p>
        </AlertDescription>
      </Alert>
    </div>
  );
}
